import React, { useMemo } from "react";

import { Icon } from "./ui";
import { getOwnerLine } from "../../../utils/ownerLine";
import useLiveWatch from "../../../hooks/useLiveWatch";

/*
  가운데 카드 아래 — 실제로 재생되는 라이브 영상 타일.

  글자만 있는 타일로 만들었더니 «관제 제품»으로 안 보였다. 영상이 깔려야 한다.
  대신 동시 재생은 4개로 묶는다. WebRTC 스트림은 한 개당 서버·PC 부담이 있어
  전부 틀면 화면이 버벅인다(실시간 관제 탭이 4×4까지 가는 것과 같은 이유).

  타일마다 watch start/ping 을 보낸다. 서버가 보는 사람 수를 세서
  아무도 안 보는 스트림을 정리하기 때문에, 빠뜨리면 집계가 어긋난다.
*/
const MAX_TILES = 4;

function LiveTile({ item, selected, onPick }) {
  // 송출 중일 때만 시청 등록한다. 꺼진 카메라에 등록하면 유령 시청자가 남는다.
  useLiveWatch(item.streamKey, item.isOnline && !!item.webRtcUrl, `tile-${item.key}`);

  return (
    <button
      type="button"
      onClick={() => onPick?.(item)}
      className={`relative min-w-0 flex-1 overflow-hidden rounded-xl bg-slate-900 text-left transition ${
        selected
          ? "ring-2 ring-blue-500 ring-offset-1 dark:ring-offset-[#0A0F1F]"
          : "border border-slate-200 dark:border-white/10"
      }`}
    >
      {item.isOnline && item.webRtcUrl ? (
        <>
          <iframe
            key={`tile-${item.streamKey}`}
            title={`tile-${item.streamKey}`}
            src={item.webRtcUrl}
            className="h-full w-full"
            style={{ border: 0, background: "black" }}
            allow="autoplay; encrypted-media"
            scrolling="no"
          />
          {/* 타일 전체가 클릭 대상이어야 한다 — iframe이 클릭을 먹지 않도록 덮는다. */}
          <span className="absolute inset-0" />
        </>
      ) : (
        <span className="flex h-full w-full flex-col items-center justify-center gap-1.5 text-white/35">
          <Icon.Camera size={22} />
          <span className="text-[11.5px]">오프라인</span>
        </span>
      )}

      {item.isOnline && (
        <span className="pointer-events-none absolute left-2 top-2 flex items-center gap-1.5 rounded-md bg-black/65 px-[7px] py-[3px] text-[10.5px] font-bold tracking-wide text-white">
          <span className="h-[5px] w-[5px] rounded-full bg-emerald-400" />
          LIVE
        </span>
      )}

      <span className="pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 to-transparent px-2.5 pb-[7px] pt-5">
        <span className="block truncate text-[11.5px] font-semibold text-white">
          {item.name}
          {item.battery != null && (
            <span className="font-normal text-white/70"> · {item.battery}%</span>
          )}
        </span>
      </span>
    </button>
  );
}

export default function CameraStrip({
  cameras = [],
  statusMap = {},
  onNavigate,
  onPick,
  pickedKey = "",
}) {
  const items = useMemo(() => {
    const list = Array.isArray(cameras) ? cameras : [];

    const mapped = list.map((cam, idx) => {
      const keys = [cam?.camIdx, cam?.id, cam?.camNo, cam?.cameraId, cam?.streamKey, idx]
        .map((v) => String(v ?? "").trim())
        .filter(Boolean);

      let meta = {};
      for (const k of keys) {
        if (statusMap[k]) {
          meta = statusMap[k] || {};
          break;
        }
      }

      const isOnline = String(meta?.liveStatus || "").toUpperCase() === "ONLINE";
      const bat = Number(meta?.battery);

      return {
        key: keys[0] || String(idx),
        streamKey: String(cam?.streamKey || "").trim(),
        webRtcUrl: String(cam?.webRtcUrl || "").trim(),
        name:
          String(meta?.workerName || "").trim() ||
          cam?.name ||
          cam?.camName ||
          cam?.camTitle ||
          `CAM${cam?.camNo || idx + 1}`,
        sub: getOwnerLine(meta) || getOwnerLine(cam) || `CAM ${cam?.camNo || idx + 1}`,
        isOnline,
        // 꺼진 카메라의 배터리는 보고된 값이 아니라 0으로 들어온다 — 띄우면 오해를 부른다.
        battery:
          isOnline && Number.isFinite(bat) && bat > 0 && bat <= 100 ? bat : null,
      };
    });

    // 송출 중을 앞으로. 전부 꺼졌을 때 줄이 통째로 비면 고장처럼 보이므로 숨기지 않는다.
    return mapped.sort((a, b) => (a.isOnline === b.isOnline ? 0 : a.isOnline ? -1 : 1));
  }, [cameras, statusMap]);

  const onlineCount = items.filter((i) => i.isOnline).length;
  const shown = items.slice(0, MAX_TILES);

  return (
    <div className="flex shrink-0 flex-col gap-2.5">
      <div className="flex items-center gap-2 px-0.5">
        <span className="text-[13px] font-semibold text-slate-500 dark:text-slate-400">
          송출 중{" "}
          <b className="font-bold text-slate-900 tabular-nums dark:text-white">
            {onlineCount}
          </b>{" "}
          / {items.length}대
        </span>
        <button
          type="button"
          onClick={() => onNavigate?.("live")}
          className="ml-auto flex items-center gap-1 text-[12.5px] font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
        >
          실시간 관제
          <Icon.Right size={13} />
        </button>
      </div>

      {items.length === 0 ? (
        <div className="flex h-[150px] items-center justify-center rounded-xl border border-dashed border-slate-300 text-[12.5px] text-slate-400 dark:border-white/15">
          등록된 카메라가 없습니다
        </div>
      ) : (
        <div className="flex h-[150px] gap-3">
          {shown.map((item) => (
            <LiveTile
              key={item.key}
              item={item}
              selected={!!pickedKey && pickedKey === item.streamKey}
              onPick={onPick}
            />
          ))}

          {/*
            예전에는 끝에 «N대 더» 칸을 뒀는데, 그 칸 때문에 영상 4개의 가로폭이
            서로 어긋나 비율이 깨졌다. 나머지 대수는 위 제목줄(«송출 중 2 / 400대»)과
            «실시간 관제» 링크로 충분하다.
          */}
        </div>
      )}
    </div>
  );
}
